/**
 * TaxonomyQueuePanel — supervisor queue for AI-vetted taxonomy suggestions.
 *
 * Lists pending_review rows from /api/directory/admin/taxonomy/queue and
 * lets the supervisor approve (keep node live) or reject (mark rejected
 * AND flip the taxonomy node status to rejected).
 *
 * Mounted at /admin/taxonomy/queue.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Sparkles,
  Clock,
  ArrowRight,
  ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const DECISION_PILL = {
  NEW_AUTO_APPROVED: "bg-emerald-100 text-emerald-800",
  MAPPED:            "bg-blue-100 text-blue-800",
  REJECTED:          "bg-red-100 text-red-700",
};

const STATUS_PILL = {
  pending_review: "bg-amber-100 text-amber-800",
  approved:       "bg-emerald-100 text-emerald-800",
  rejected:       "bg-red-100 text-red-700",
};

export default function TaxonomyQueuePanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = async (status) => {
    setLoading(true);
    try {
      const q = status ? `?status=${encodeURIComponent(status)}` : "";
      const r = await fetch(`${API}/api/directory/admin/taxonomy/queue${q}`);
      const d = await r.json();
      setRows(d.rows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const act = async (qid, action) => {
    setActingId(qid);
    try {
      await fetch(`${API}/api/directory/admin/taxonomy/approve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queue_id: qid, action }),
      });
      load(filter);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="taxonomy-queue-page">
      <header className="bg-gradient-to-r from-stone-900 to-stone-800 text-white">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Link to="/directory" className="text-[12px] text-stone-400 hover:text-white">← Directory</Link>
          <div className="flex items-center gap-2 mt-3 mb-1">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-amber-400">Supervisor Queue</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">AI-vetted Taxonomy Suggestions</h1>
          <p className="text-stone-300 mt-2 max-w-2xl text-[14px]">
            Every "Other → AI vet" submission from <code>/add-business</code> arrives here. Auto-approved suggestions
            already created live taxonomy nodes — your approve/reject either confirms or retracts.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {[
            ["pending_review", "Pending review"],
            ["approved",       "Approved"],
            ["rejected",       "Rejected"],
            ["",               "All"],
          ].map(([k, label]) => (
            <button
              key={k || "all"}
              onClick={() => setFilter(k)}
              data-testid={`tax-queue-filter-${k || "all"}`}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${filter === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"}`}
            >
              {label}
            </button>
          ))}
          <div className="ml-auto text-[12px] text-stone-500">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
        </div>

        {loading && <div className="text-center py-8 text-stone-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>}

        {!loading && rows.length === 0 && (
          <div className="bg-white rounded-2xl border-2 border-dashed border-stone-300 p-10 text-center text-stone-500">
            <Clock className="w-10 h-10 mx-auto text-stone-300 mb-2" />
            Nothing to review.
          </div>
        )}

        <div className="space-y-3" data-testid="tax-queue-list">
          {rows.map((r) => (
            <div key={r._id} className="bg-white rounded-2xl border border-stone-200 p-5 shadow-sm" data-testid={`tax-queue-row-${r._id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${DECISION_PILL[r.decision] || "bg-stone-100"}`}>
                      {r.decision}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${STATUS_PILL[r.status] || "bg-stone-100"}`}>
                      {r.status}
                    </span>
                    <span className="text-[11px] text-stone-400">{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-[15px] font-bold text-stone-900">"{r.suggestion}"</div>
                  {r.source?.business_name && (
                    <div className="text-[12px] text-stone-500">
                      from <strong>{r.source.business_name}</strong>
                      {r.source.website && (
                        <> · <a href={r.source.website} target="_blank" rel="noopener noreferrer" className="underline">{r.source.website}</a></>
                      )}
                    </div>
                  )}
                </div>
                {r.status === "pending_review" && (
                  <div className="shrink-0 flex gap-2">
                    <button onClick={() => act(r._id, "approve")} disabled={actingId === r._id} data-testid={`tax-queue-approve-${r._id}`} className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-[12px] font-bold hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button onClick={() => act(r._id, "reject")} disabled={actingId === r._id} data-testid={`tax-queue-reject-${r._id}`} className="px-3 py-1.5 rounded-full bg-red-600 text-white text-[12px] font-bold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1">
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
                <div className="rounded-lg bg-stone-50 border border-stone-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1">AI decision</div>
                  <div className="space-y-0.5">
                    {r.vet?.decision === "MAPPED" && (
                      <div><strong>{r.vet.mapped_master}</strong> / {r.vet.mapped_niche}{r.vet.mapped_category ? ` / ${r.vet.mapped_category}` : ""}</div>
                    )}
                    {r.vet?.decision === "NEW" && (
                      <div>New <strong>{r.vet.new_level}</strong>: <strong>{r.vet.canonical_name}</strong>{r.vet.new_parent_master ? ` (under ${r.vet.new_parent_master}${r.vet.new_parent_niche ? `/${r.vet.new_parent_niche}` : ""})` : ""}</div>
                    )}
                    <div className="text-[11px] text-stone-500">template: <code>{r.vet?.template_id || "—"}</code> · tone: <code>{r.vet?.default_wording_tone || "—"}</code></div>
                  </div>
                </div>
                <div className="rounded-lg bg-fuchsia-50 border border-fuchsia-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-fuchsia-700 mb-1">Reasoning</div>
                  <div className="text-stone-800">{r.vet?.reasoning || "(none)"}</div>
                </div>
              </div>
              {r.created_taxonomy_id && (
                <div className="mt-2 text-[11px] text-stone-500">
                  Taxonomy node: <code>{r.created_taxonomy_id}</code>
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
